import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import {
  ArrowPathIcon,
  XCircleIcon,
  UserIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { Drawer } from '../../components/ui/Drawer';
import adminService, {
  ImpersonationActivityLog,
  ImpersonationActivityStatsResponse,
} from '../../services/admin.service';
import {
  LOG_FETCH_ALL_LIMIT,
  LOG_AUTO_REFRESH_MS,
  DetailField,
  NONE,
  formatJsonMaybe,
} from './logPageShared';

/**
 * Logging → Impersonation Activity.
 *
 * Pull-all + TanTable virtualization, detail in a slide-out drawer — same
 * pattern as System Logs. Request params and user agent live in the drawer; row
 * cells stay single-line for the virtualizer.
 */

const getMethodBadgeColor = (method: string) => {
  switch (method) {
    case 'GET':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
    case 'POST':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
    case 'PUT':
    case 'PATCH':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800';
    case 'DELETE':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
    default:
      return 'bg-gray-100 text-gray-800 border-rule dark:bg-gray-700 dark:text-gray-200';
  }
};

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
};

const ImpersonationActivity: React.FC = () => {
  const [logs, setLogs] = useState<ImpersonationActivityLog[]>([]);
  const [stats, setStats] = useState<ImpersonationActivityStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selected, setSelected] = useState<ImpersonationActivityLog | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logsAbortRef = useRef<AbortController | null>(null);

  const fetchLogs = useCallback(async () => {
    logsAbortRef.current?.abort();
    const controller = new AbortController();
    logsAbortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await adminService.getImpersonationActivity({
        limit: LOG_FETCH_ALL_LIMIT,
      });
      if (controller.signal.aborted) return;
      setLogs(response.logs);
      setTotalLogs(response.total);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || 'Failed to fetch impersonation activity logs');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await adminService.getImpersonationActivityStats());
    } catch (err: any) {
      console.error('Failed to fetch impersonation activity stats:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchLogs();
    await fetchStats();
  }, [fetchLogs, fetchStats]);

  useEffect(() => {
    refresh();
    return () => logsAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVisibilityAwareInterval(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, autoRefresh ? LOG_AUTO_REFRESH_MS : null);

  const columns = useMemo<ColumnDef<ImpersonationActivityLog>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Timestamp',
        cell: (info) => (
          <span className="whitespace-nowrap">{formatTimestamp(info.getValue() as string)}</span>
        ),
        size: 175,
      },
      {
        accessorKey: 'admin_email',
        header: 'Admin',
        cell: (info) => {
          const v = info.getValue() as string;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 220,
      },
      {
        accessorKey: 'impersonated_email',
        header: 'Impersonated User',
        cell: (info) => {
          const v = info.getValue() as string;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 220,
      },
      {
        accessorKey: 'http_method',
        header: 'Method',
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium border ${getMethodBadgeColor(v)}`}
            >
              {v}
            </span>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'endpoint',
        header: 'Endpoint',
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 340,
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 240,
      },
      {
        accessorKey: 'session_id',
        header: 'Session',
        cell: (info) => info.getValue() as number,
        size: 100,
      },
    ],
    []
  );

  const truncated = totalLogs > logs.length;

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <UserIcon className="w-7 h-7 text-purple-600" />
            <h1 className="text-xl font-bold text-heading">
              Impersonation Activity
            </h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Actions performed during user impersonation sessions. Click any row for the request
            detail.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={`Auto-refresh every ${LOG_AUTO_REFRESH_MS / 1000}s`}
            className={`px-3 py-2 rounded-md border text-sm ${
              autoRefresh
                ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300'
                : 'bg-gray-50 border-gray-300 text-label dark:bg-gray-700 dark:border-gray-600'
            }`}
          >
            Auto: {autoRefresh ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Activity</div>
            <div className="text-2xl font-bold text-heading">
              {stats.total_logs.toLocaleString()}
            </div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Sessions</div>
            <div className="text-2xl font-bold text-purple-600">{stats.total_sessions}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Active Sessions</div>
            <div className="text-2xl font-bold text-green-600">{stats.active_sessions}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Actions (24h)</div>
            <div className="text-2xl font-bold text-blue-600">{stats.recent_actions_24h}</div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
            <XCircleIcon className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {truncated && (
        <div className="mb-2 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          Showing the {logs.length.toLocaleString()} most recent of{' '}
          {totalLogs.toLocaleString()} entries.
        </div>
      )}

      <TanTable
        data={logs}
        columns={columns}
        loading={loading}
        settingsKey="impersonation-activity-table"
        fieldName="impersonation-activity-table"
        cardTitle="Impersonation Activity"
        searchPlaceholder="Search admin, user, endpoint…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        columnFilterTypes={{
          created_at: 'text',
          admin_email: 'text',
          impersonated_email: 'text',
          endpoint: 'text',
          action: 'text',
          session_id: 'text',
        }}
        defaultSorting={[{ id: 'created_at', desc: true }]}
        onRowClick={(row: ImpersonationActivityLog) => setSelected(row)}
      />

      <Drawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="xl"
        width="56rem"
        title={
          selected ? (
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium border ${getMethodBadgeColor(selected.http_method)}`}
              >
                {selected.http_method}
              </span>
              <span className="truncate">{selected.endpoint}</span>
              <span className="text-xs text-muted shrink-0">
                {formatTimestamp(selected.created_at)}
              </span>
            </div>
          ) : (
            'Impersonation Activity'
          )
        }
      >
        {selected && (
          <div className="space-y-5 text-sm">
            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Activity
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                <DetailField label="Admin">{selected.admin_email}</DetailField>
                <DetailField label="Impersonated User">
                  {selected.impersonated_email}
                </DetailField>
                <DetailField label="Endpoint">
                  {selected.http_method} {selected.endpoint}
                </DetailField>
                <DetailField label="Action">{selected.action || NONE}</DetailField>
                <DetailField label="Session ID">{selected.session_id}</DetailField>
                <DetailField label="Entry ID">{selected.id}</DetailField>
                <DetailField label="IP Address">{selected.ip_address || NONE}</DetailField>
                <DetailField label="Timestamp">
                  {formatTimestamp(selected.created_at)}
                </DetailField>
              </dl>
            </section>

            {selected.request_params && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Request Parameters
                </div>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                  {formatJsonMaybe(selected.request_params)}
                </pre>
              </section>
            )}

            {selected.user_agent && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  User Agent
                </div>
                <div className="text-xs text-heading break-all">
                  {selected.user_agent}
                </div>
              </section>
            )}
          </div>
        )}
      </Drawer>

      {logs.length === 0 && !loading && (
        <div className="text-center py-12">
          <InformationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No impersonation activity found</p>
        </div>
      )}
    </div>
  );
};

export default ImpersonationActivity;
